import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Activity, CheckCircle2, UserPlus, FolderKanban, Globe, Inbox, LayoutDashboard, ListChecks, LogOut, Menu as MenuIcon, Search, Settings, ShieldCheck, ShoppingBag, SquareKanban, UserRound, Users, X } from 'lucide-react';
import { roleLabel } from '@rv/shared';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Avatar, cx } from '../components/ui';
import { NotificationBell } from '../components/NotificationBell';
import { ThemeToggle } from '../components/ThemeToggle';

export function BrandMark({ size = 28 }: { size?: number }) {
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center rounded-[9px] bg-accent-600 text-white shadow-btn" style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 24 24" width={size * 0.58} height={size * 0.58} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21s-6.5-5.6-6.5-11A6.5 6.5 0 0 1 18.5 10c0 5.4-6.5 11-6.5 11Z" />
        <path d="m9.3 10.2 1.9 1.9 3.6-3.7" />
      </svg>
    </span>
  );
}

export function Brand({ compact }: { compact?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <BrandMark />
      {!compact && (
        <span className="leading-tight">
          <span className="block text-[14px] font-semibold tracking-[-0.01em] text-ink">Design Review</span>
          <span className="block text-[11px] text-slate-500">Review · Approve · Ship</span>
        </span>
      )}
    </span>
  );
}

export function UserMenu({ area }: { area: 'admin' | 'portal' }) {
  const me = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const role = roleLabel(me.user.globalRole, me.user.specialty);
  return (
    <Menu.Root>
      <Menu.Trigger className="flex items-center gap-2 rounded-full py-1 pr-2.5 pl-1 transition-colors hover:bg-slate-900/[0.05]" aria-label="Account menu">
        <Avatar name={me.user.name || me.user.email} size={28} />
        <span className="hidden max-w-[140px] truncate text-[13px] font-medium text-slate-700 sm:block">{me.user.name || me.user.email}</span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="end" sideOffset={8} className="z-50 min-w-[230px] animate-pop-in rounded-xl border border-line bg-white p-1.5 shadow-pop">
          <div className="flex items-center gap-2.5 px-2.5 py-2">
            <Avatar name={me.user.name || me.user.email} size={32} />
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-ink">{me.user.name || 'Your account'}</p>
              <p className="truncate text-[12px] text-slate-500">{me.user.email}</p>
            </div>
          </div>
          <p className="mx-2.5 mb-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{role}</p>
          <Menu.Separator className="my-1 h-px bg-line" />
          <Menu.Item onSelect={() => nav(`/${area}/account`)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] text-slate-700 outline-none data-[highlighted]:bg-slate-100">
            <UserRound className="size-3.5" /> {t('nav.account')}
          </Menu.Item>
          <Menu.Item
            onSelect={async () => {
              await api('/auth/logout', { body: {} }).catch(() => undefined);
              qc.clear();
              nav('/login');
            }}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] text-rose-700 outline-none data-[highlighted]:bg-rose-50"
          >
            <LogOut className="size-3.5" /> {t('nav.signOut')}
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** Header search: jumps to Projects filtered by the query (Ctrl/⌘ K focuses it). */
function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        document.getElementById('global-search')?.focus();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  return (
    <form
      role="search"
      className="relative hidden w-full max-w-sm md:block"
      onSubmit={(e) => {
        e.preventDefault();
        nav(`/admin/projects?q=${encodeURIComponent(q.trim())}`);
      }}
    >
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
      <input id="global-search" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects and clients…" className="h-9 w-full rounded-lg border border-transparent bg-slate-900/[0.04] pr-14 pl-9 text-[13px] text-ink placeholder:text-slate-400 transition-colors hover:bg-slate-900/[0.06] focus:border-accent-500 focus:bg-white focus:ring-4 focus:ring-accent-100 focus:outline-none" />
      <kbd className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded border border-line bg-white px-1.5 py-0.5 font-sans text-[10.5px] font-medium text-slate-400">Ctrl K</kbd>
    </form>
  );
}

export function AdminLayout() {
  const me = useAuth();
  const { t } = useTranslation();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [loc.pathname]);
  // Pending "Request access" asks from reviewed websites (admins only).
  const requests = useQuery({ queryKey: ['access-requests', false], queryFn: () => api('/access-requests'), enabled: me.user.globalRole === 'admin', refetchInterval: 60_000 });
  const pendingRequests: number = requests.data?.pending ?? 0;
  const groups: { label: string; items: { to: string; label: string; icon: typeof Users; end?: boolean; badge?: number }[] }[] = [
    {
      label: 'Workspace',
      items: [
        { to: '/admin', label: t('nav.dashboard'), icon: LayoutDashboard, end: true },
        { to: '/admin/projects', label: t('nav.projects'), icon: FolderKanban },
      ],
    },
    {
      label: 'Review',
      items: [
        { to: '/admin/inbox', label: t('nav.inbox'), icon: Inbox },
        { to: '/admin/qa', label: t('nav.qa'), icon: SquareKanban },
        { to: '/admin/picklists', label: t('nav.picklists'), icon: ListChecks },
        { to: '/admin/integrations', label: t('nav.integrations'), icon: Globe },
        { to: '/admin/shopify-apps', label: t('nav.shopifyApps'), icon: ShoppingBag },
        { to: '/admin/approvals', label: t('nav.approvals'), icon: CheckCircle2 },
      ],
    },
    ...(me.user.globalRole === 'admin'
      ? [
          {
            label: 'Administration',
            items: [
              { to: '/admin/users', label: t('nav.users'), icon: Users },
              { to: '/admin/roles', label: t('nav.roles'), icon: ShieldCheck },
              { to: '/admin/requests', label: 'Access requests', icon: UserPlus, badge: pendingRequests },
              { to: '/admin/activity', label: t('nav.activity'), icon: Activity },
              { to: '/admin/settings', label: t('nav.settings'), icon: Settings },
            ],
          },
        ]
      : []),
  ];
  const nav = (
    <nav className="flex flex-col gap-5 px-3 py-4" aria-label="Main">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="mb-1.5 px-2.5 text-[11px] font-semibold tracking-[0.06em] text-slate-400 uppercase">{g.label}</p>
          <div className="flex flex-col gap-0.5">
            {g.items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                className={({ isActive }) =>
                  cx(
                    'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13.5px] font-medium transition-colors',
                    isActive ? 'bg-white text-ink shadow-card ring-1 ring-line' : 'text-slate-600 hover:bg-slate-900/[0.04] hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <i.icon className={cx('size-[17px] transition-colors', isActive ? 'text-accent-600' : 'text-slate-400 group-hover:text-slate-600')} />
                    {i.label}
                    {!!i.badge && <span className="ml-auto rounded-full bg-accent-600 px-1.5 py-0.5 text-[10.5px] font-semibold text-white" aria-label={`${i.badge} pending`}>{i.badge}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
  const footer = (
    <div className="border-t border-line p-3">
      <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
        <Avatar name={me.user.name || me.user.email} size={30} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-ink">{me.user.name || me.user.email}</p>
          <p className="truncate text-[11.5px] text-slate-500">{roleLabel(me.user.globalRole, me.user.specialty)}</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 hidden h-screen w-[248px] shrink-0 flex-col border-r border-line bg-sidebar lg:flex">
        <div className="flex h-16 items-center px-5">
          <Brand />
        </div>
        <div className="scrollbar-thin flex-1 overflow-y-auto">{nav}</div>
        {footer}
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 animate-fade-in bg-slate-950/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[272px] animate-pop-in flex-col bg-sidebar shadow-pop">
            <div className="flex h-16 items-center justify-between px-5">
              <Brand />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
                <X className="size-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{nav}</div>
            {footer}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <button className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu">
            <MenuIcon className="size-5" />
          </button>
          <div className="lg:hidden">
            <Brand compact />
          </div>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-1.5">
            <ThemeToggle />
            <NotificationBell linkBase="admin" />
            <span className="mx-1 hidden h-6 w-px bg-line sm:block" />
            <UserMenu area="admin" />
          </div>
        </header>
        {/* The QA board uses the full width for its columns. */}
        <main className={cx('mx-auto w-full flex-1 px-4 py-7 sm:px-8', !loc.pathname.startsWith('/admin/qa') && 'max-w-[1320px]')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
